import * as z from "zod/v4";

export const reclaimAccessModes = ["trial", "free"] as const;
export type ReclaimAccessMode = (typeof reclaimAccessModes)[number];

export const disputesAccessOverrides = [
  "default",
  "trial",
  "free",
  "paid",
] as const;
export type DisputesAccessOverride = (typeof disputesAccessOverrides)[number];

export const reclaimAccessSources = [
  "intake_modal",
  "placeholder_landing",
  "shopify_reclaim_install",
] as const;
export type ReclaimAccessSource = (typeof reclaimAccessSources)[number];

export const reclaimActivationEntrySources = [
  "intake_modal",
  "insights_page_cta",
  "shopify_reclaim_install",
] as const;
export type ReclaimActivationEntrySource =
  (typeof reclaimActivationEntrySources)[number];

export function getReclaimActivationSourceForEntrySource(
  entrySource: ReclaimActivationEntrySource,
): ReclaimAccessSource {
  switch (entrySource) {
    case "insights_page_cta":
      return "placeholder_landing";
    case "shopify_reclaim_install":
      return "shopify_reclaim_install";
    default:
      return "intake_modal";
  }
}

export const reclaimSourceRulePricingModes = ["default", "custom"] as const;
export type ReclaimSourceRulePricingMode =
  (typeof reclaimSourceRulePricingModes)[number];

export interface ReclaimPricingTerms {
  successFeePercentage: number;
  minFeeDollars: number;
}

export interface ReclaimSourceRule {
  accessMode: DisputesAccessOverride;
  pricingMode: ReclaimSourceRulePricingMode;
  successFeePercentage?: number;
  minFeeDollars?: number;
}

export type ReclaimSourceRules = Record<ReclaimAccessSource, ReclaimSourceRule>;

export interface ReclaimAccessDefaults {
  accessMode: ReclaimAccessMode;
  trialMonths: number;
  successFeePercentage: number;
  minFeeDollars: number;
  sourceRules: ReclaimSourceRules;
}

export interface ReclaimResolvedTerms extends ReclaimPricingTerms {
  accessMode: ReclaimAccessMode | "paid";
  trialMonths: number;
}

export interface ReclaimPricingOverride extends ReclaimPricingTerms {
  source?: string;
  appliedAt?: Date;
}

export interface ReclaimSourceMaterialization {
  accessOverride?: DisputesAccessOverride;
  pricingOverride?: ReclaimPricingOverride;
}

export interface ReclaimCommercialTermsDisputes {
  accessOverride?: DisputesAccessOverride | null;
  freeAccessEnabled?: boolean | null;
  pricingOverride?: {
    successFeePercentage?: number | null;
    minFeeDollars?: number | null;
  } | null;
  billing?: {
    feePercentage?: number | null;
    minFeeDollars?: number | null;
  } | null;
}

export const DEFAULT_RECLAIM_TRIAL_MONTHS = 6;
export const DEFAULT_RECLAIM_SUCCESS_FEE_PERCENTAGE = 25;
export const DEFAULT_RECLAIM_MIN_FEE_DOLLARS = 0;

export const DEFAULT_RECLAIM_SOURCE_RULES: ReclaimSourceRules = {
  intake_modal: { accessMode: "free", pricingMode: "default" },
  placeholder_landing: { accessMode: "paid", pricingMode: "default" },
  shopify_reclaim_install: { accessMode: "default", pricingMode: "default" },
};

export const DEFAULT_RECLAIM_ACCESS_DEFAULTS: ReclaimAccessDefaults = {
  accessMode: "trial",
  trialMonths: DEFAULT_RECLAIM_TRIAL_MONTHS,
  successFeePercentage: DEFAULT_RECLAIM_SUCCESS_FEE_PERCENTAGE,
  minFeeDollars: DEFAULT_RECLAIM_MIN_FEE_DOLLARS,
  sourceRules: DEFAULT_RECLAIM_SOURCE_RULES,
};

export const reclaimPricingTermsSchema = z.object({
  successFeePercentage: z.number().min(0).max(100),
  minFeeDollars: z.number().min(0),
});

export const reclaimSourceRuleSchema = z.object({
  accessMode: z.enum(disputesAccessOverrides),
  pricingMode: z.enum(reclaimSourceRulePricingModes),
  successFeePercentage: z.number().min(0).max(100).optional(),
  minFeeDollars: z.number().min(0).optional(),
});

export const reclaimSourceRulesSchema = z.object({
  intake_modal: reclaimSourceRuleSchema,
  placeholder_landing: reclaimSourceRuleSchema,
  shopify_reclaim_install: reclaimSourceRuleSchema,
});

export const reclaimAccessDefaultsSchema = z.object({
  accessMode: z.enum(reclaimAccessModes),
  trialMonths: z.number().int().min(1).max(120),
  successFeePercentage: z.number().min(0).max(100),
  minFeeDollars: z.number().min(0),
  sourceRules: reclaimSourceRulesSchema,
});

export const merchantReclaimAccessDefaultsSchema = z.object({
  accessMode: z.enum(reclaimAccessModes),
  trialMonths: z.number().int().min(1).max(120),
  successFeePercentage: z.number().min(0).max(100),
  minFeeDollars: z.number().min(0),
});

export type MerchantReclaimAccessDefaults = Omit<
  ReclaimAccessDefaults,
  "sourceRules"
>;

export function hydrateMerchantReclaimAccessDefaults(
  merchantDefaults: MerchantReclaimAccessDefaults,
): ReclaimAccessDefaults {
  return { ...merchantDefaults, sourceRules: DEFAULT_RECLAIM_SOURCE_RULES };
}

export function normalizeReclaimAccessDefaults(
  input: unknown,
): ReclaimAccessDefaults {
  const overrides =
    input && typeof input === "object" && !Array.isArray(input)
      ? Object.fromEntries(Object.entries(input))
      : {};
  const sourceRulesInput: Record<string, unknown> =
    overrides.sourceRules &&
    typeof overrides.sourceRules === "object" &&
    !Array.isArray(overrides.sourceRules)
      ? (overrides.sourceRules as Record<string, unknown>)
      : {};
  const successFeePercentage =
    typeof overrides.successFeePercentage === "number"
      ? overrides.successFeePercentage
      : DEFAULT_RECLAIM_ACCESS_DEFAULTS.successFeePercentage;
  const minFeeDollars =
    typeof overrides.minFeeDollars === "number"
      ? overrides.minFeeDollars
      : DEFAULT_RECLAIM_ACCESS_DEFAULTS.minFeeDollars;
  const mergedDefaults = {
    ...DEFAULT_RECLAIM_ACCESS_DEFAULTS,
    ...overrides,
    sourceRules: reclaimAccessSources.reduce<Partial<ReclaimSourceRules>>(
      (sourceRules, source) => {
        const rawSourceRule = sourceRulesInput[source];
        const sourceRuleInput =
          rawSourceRule &&
          typeof rawSourceRule === "object" &&
          !Array.isArray(rawSourceRule)
            ? Object.fromEntries(Object.entries(rawSourceRule))
            : {};
        const rule = {
          ...DEFAULT_RECLAIM_SOURCE_RULES[source],
          ...sourceRuleInput,
        };
        sourceRules[source] =
          rule.pricingMode === "custom"
            ? {
                ...rule,
                successFeePercentage:
                  rule.successFeePercentage ?? successFeePercentage,
                minFeeDollars: rule.minFeeDollars ?? minFeeDollars,
              }
            : { accessMode: rule.accessMode, pricingMode: "default" };
        return sourceRules;
      },
      {},
    ),
  };
  return reclaimAccessDefaultsSchema.parse(mergedDefaults);
}

export function resolveReclaimSourceRule(
  accessDefaults: ReclaimAccessDefaults,
  source: ReclaimAccessSource,
): ReclaimSourceRule {
  return (
    accessDefaults.sourceRules?.[source] ?? DEFAULT_RECLAIM_SOURCE_RULES[source]
  );
}

export function resolveReclaimTerms(
  accessDefaults: ReclaimAccessDefaults,
  source?: ReclaimAccessSource,
): ReclaimResolvedTerms {
  const sourceRule = source
    ? resolveReclaimSourceRule(accessDefaults, source)
    : undefined;
  const accessMode =
    sourceRule && sourceRule.accessMode !== "default"
      ? sourceRule.accessMode
      : accessDefaults.accessMode;
  return {
    accessMode,
    trialMonths: accessDefaults.trialMonths,
    ...resolveReclaimPricingTerms(accessDefaults, sourceRule),
  };
}

export function resolveReclaimCommercialTerms({
  accessDefaults,
  disputes,
  source,
}: {
  accessDefaults: ReclaimAccessDefaults;
  disputes?: ReclaimCommercialTermsDisputes | null;
  source?: ReclaimAccessSource;
}): ReclaimResolvedTerms {
  const sourceTerms = resolveReclaimTerms(accessDefaults, source);

  return {
    accessMode: resolveReclaimCommercialAccessMode(disputes, sourceTerms),
    trialMonths: sourceTerms.trialMonths,
    ...resolveReclaimCommercialPricing(disputes, sourceTerms),
  };
}

export function resolveReclaimPricingTerms(
  accessDefaults: ReclaimAccessDefaults,
  sourceRule?: ReclaimSourceRule,
): ReclaimPricingTerms {
  if (sourceRule?.pricingMode === "custom") {
    return reclaimPricingTermsSchema.parse({
      successFeePercentage:
        sourceRule.successFeePercentage ?? accessDefaults.successFeePercentage,
      minFeeDollars: sourceRule.minFeeDollars ?? accessDefaults.minFeeDollars,
    });
  }
  return {
    successFeePercentage: accessDefaults.successFeePercentage,
    minFeeDollars: accessDefaults.minFeeDollars,
  };
}

export function getReclaimSourceMaterialization(
  accessDefaults: ReclaimAccessDefaults,
  source: ReclaimAccessSource,
  appliedAt: Date,
): ReclaimSourceMaterialization {
  const sourceRule = resolveReclaimSourceRule(accessDefaults, source);
  const terms = resolveReclaimTerms(accessDefaults, source);
  const materialization: ReclaimSourceMaterialization = {};

  if (sourceRule.accessMode !== "default") {
    materialization.accessOverride = sourceRule.accessMode;
  }

  if (sourceRule.pricingMode === "custom") {
    materialization.pricingOverride = {
      successFeePercentage: terms.successFeePercentage,
      minFeeDollars: terms.minFeeDollars,
      source,
      appliedAt,
    };
  }

  return materialization;
}

function resolveReclaimCommercialAccessMode(
  disputes: ReclaimCommercialTermsDisputes | null | undefined,
  sourceTerms: ReclaimResolvedTerms,
): ReclaimResolvedTerms["accessMode"] {
  if (disputes?.accessOverride === "paid") {
    return "paid";
  }
  if (
    disputes?.accessOverride === "free" ||
    disputes?.freeAccessEnabled === true
  ) {
    return "free";
  }
  if (disputes?.accessOverride === "trial") {
    return "trial";
  }
  return sourceTerms.accessMode;
}

function resolveReclaimCommercialPricing(
  disputes: ReclaimCommercialTermsDisputes | null | undefined,
  sourceTerms: ReclaimResolvedTerms,
): ReclaimPricingTerms {
  if (
    disputes?.pricingOverride?.successFeePercentage != null &&
    disputes.pricingOverride.minFeeDollars != null
  ) {
    return {
      successFeePercentage: disputes.pricingOverride.successFeePercentage,
      minFeeDollars: disputes.pricingOverride.minFeeDollars,
    };
  }

  if (
    disputes?.billing?.feePercentage != null ||
    disputes?.billing?.minFeeDollars != null
  ) {
    return {
      successFeePercentage:
        disputes.billing.feePercentage ?? sourceTerms.successFeePercentage,
      minFeeDollars:
        disputes.billing.minFeeDollars ?? sourceTerms.minFeeDollars,
    };
  }

  return {
    successFeePercentage: sourceTerms.successFeePercentage,
    minFeeDollars: sourceTerms.minFeeDollars,
  };
}
